import React, { useState, useEffect, useRef } from 'react';
import { useAuth, API } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Slider } from '../components/ui/slider';
import { 
  Upload, Image as ImageIcon, Wand2, RefreshCw, Download,
  ChevronLeft, ChevronRight, Layers, Palette, Check, X, Loader2
} from 'lucide-react';

const SurfaceReplacement = () => {
  const { user, isAuthenticated, getAuthHeaders } = useAuth();
  
  // State
  const [slabs, setSlabs] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [selectedSlab, setSelectedSlab] = useState(null);
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [loading, setLoading] = useState(false);
  const [replacing, setReplacing] = useState(false);
  const [result, setResult] = useState(null);
  const [sliderPosition, setSliderPosition] = useState(50);
  
  // Settings
  const [alignVeins, setAlignVeins] = useState(true);
  const [matchLighting, setMatchLighting] = useState(true);
  const [blendMode, setBlendMode] = useState('alpha');
  const [featherRadius, setFeatherRadius] = useState(15);
  
  // Refs
  const containerRef = useRef(null);
  
  useEffect(() => {
    fetchSlabs();
    fetchMaterials();
  }, []);
  
  const fetchSlabs = async () => {
    try {
      const res = await axios.get(`${API}/sre-v2/slabs?has_segmentation=true&limit=20`);
      setSlabs(res.data.slabs || []);
    } catch (err) {
      console.error('Failed to fetch slabs:', err);
    }
  };
  
  const fetchMaterials = async () => {
    try {
      const res = await axios.get(`${API}/sre-v2/materials/for-replacement?limit=30`);
      setMaterials(res.data.materials || []);
    } catch (err) {
      console.error('Failed to fetch materials:', err);
    }
  };
  
  const loadSlab = async (slab) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/sre-v2/slabs/${slab.id}`);
      setSelectedSlab(res.data);
      setResult(null);
    } catch (err) {
      toast.error('Failed to load slab');
    } finally {
      setLoading(false);
    }
  };
  
  const handleReplace = async () => {
    if (!selectedSlab || !selectedMaterial) {
      toast.error('Select both a slab and a material');
      return;
    }
    
    setReplacing(true);
    try {
      const headers = await getAuthHeaders();
      const res = await axios.post(
        `${API}/sre-v2/slabs/${selectedSlab.slab.id}/replace-surface`,
        {
          material_id: selectedMaterial.material_id,
          blend_mode: blendMode,
          feather_radius: featherRadius,
          align_veins: alignVeins,
          match_lighting: matchLighting
        },
        { headers, timeout: 120000 }
      );
      
      if (res.data.success) {
        setResult(res.data);
        toast.success(`Replacement complete: ${res.data.material.name}`);
      } else {
        toast.error(res.data.detail || 'Replacement failed');
      }
    } catch (err) {
      console.error('Replacement error:', err);
      toast.error(err.response?.data?.detail || 'Replacement failed');
    } finally {
      setReplacing(false);
    }
  };
  
  const downloadResult = () => {
    if (result?.images?.after) {
      const link = document.createElement('a');
      link.href = result.images.after;
      link.download = `replacement_${selectedMaterial?.name || 'result'}.jpg`;
      link.click();
    }
  };
  
  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <Header />
      
      <main className="container mx-auto px-4 py-8">
        {/* Title */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-light tracking-wide text-white mb-2">
            Surface Replacement Engine
          </h1>
          <p className="text-gray-400">
            Replace segmented surfaces with real slab materials
          </p>
        </div>
        
        <div className="grid lg:grid-cols-4 gap-6">
          {/* Left Panel - Slab Selection */}
          <div className="lg:col-span-1 space-y-4">
            <div className="bg-[#111] rounded-xl p-4 border border-[#222]">
              <h3 className="text-sm font-medium text-[#D4AF37] mb-3 flex items-center gap-2">
                <Layers className="w-4 h-4" />
                1. SELECT SLAB
              </h3>
              
              <div className="space-y-2 max-h-[200px] overflow-y-auto">
                {slabs.map(slab => (
                  <button
                    key={slab.id}
                    onClick={() => loadSlab(slab)}
                    className={`w-full p-2 rounded-lg text-left text-sm transition-all ${
                      selectedSlab?.slab?.id === slab.id
                        ? 'bg-[#D4AF37]/20 border border-[#D4AF37]'
                        : 'bg-[#1A1A1A] border border-transparent hover:border-[#333]'
                    }`}
                  >
                    <div className="font-medium truncate">{slab.commercial_name || slab.id.slice(0, 8)}</div>
                    <div className="text-xs text-gray-500">{slab.segmentation_status || 'segmented'}</div>
                  </button>
                ))}
                
                {slabs.length === 0 && (
                  <p className="text-gray-500 text-sm text-center py-4">
                    No segmented slabs available
                  </p>
                )}
              </div>
            </div>
            
            {/* Material Selection */}
            <div className="bg-[#111] rounded-xl p-4 border border-[#222]">
              <h3 className="text-sm font-medium text-[#D4AF37] mb-3 flex items-center gap-2">
                <Palette className="w-4 h-4" />
                2. SELECT MATERIAL
              </h3>
              
              <div className="grid grid-cols-3 gap-2 max-h-[300px] overflow-y-auto">
                {materials.map(mat => (
                  <button
                    key={mat.material_id}
                    onClick={() => setSelectedMaterial(mat)}
                    className={`aspect-square rounded-lg overflow-hidden border-2 transition-all ${
                      selectedMaterial?.material_id === mat.material_id
                        ? 'border-[#D4AF37] ring-2 ring-[#D4AF37]/50'
                        : 'border-transparent hover:border-[#333]'
                    }`}
                  >
                    <img 
                      src={mat.image_url} 
                      alt={mat.name}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </button>
                ))}
              </div>
              
              {selectedMaterial && (
                <div className="mt-3 p-2 bg-[#1A1A1A] rounded-lg">
                  <div className="font-medium text-sm">{selectedMaterial.name}</div>
                  <div className="text-xs text-gray-500">{selectedMaterial.stone_type}</div>
                </div>
              )}
            </div>
            
            {/* Settings */}
            <div className="bg-[#111] rounded-xl p-4 border border-[#222]">
              <h3 className="text-sm font-medium text-[#D4AF37] mb-3">
                3. SETTINGS
              </h3>
              
              <div className="space-y-3">
                <label className="flex items-center justify-between">
                  <span className="text-sm text-gray-300">Align Veins</span>
                  <button
                    onClick={() => setAlignVeins(!alignVeins)}
                    className={`w-10 h-5 rounded-full transition-colors ${
                      alignVeins ? 'bg-[#D4AF37]' : 'bg-[#333]'
                    }`}
                  >
                    <div className={`w-4 h-4 bg-white rounded-full transition-transform ${
                      alignVeins ? 'translate-x-5' : 'translate-x-0.5'
                    }`} />
                  </button>
                </label>
                
                <label className="flex items-center justify-between">
                  <span className="text-sm text-gray-300">Match Lighting</span>
                  <button
                    onClick={() => setMatchLighting(!matchLighting)}
                    className={`w-10 h-5 rounded-full transition-colors ${
                      matchLighting ? 'bg-[#D4AF37]' : 'bg-[#333]'
                    }`}
                  >
                    <div className={`w-4 h-4 bg-white rounded-full transition-transform ${
                      matchLighting ? 'translate-x-5' : 'translate-x-0.5'
                    }`} />
                  </button>
                </label>
                
                <div>
                  <label className="text-sm text-gray-300">Feather Radius: {featherRadius}px</label>
                  <input
                    type="range"
                    min="0"
                    max="50"
                    value={featherRadius}
                    onChange={(e) => setFeatherRadius(parseInt(e.target.value))}
                    className="w-full mt-1"
                  />
                </div>
              </div>
            </div>
            
            {/* Replace Button */}
            <Button
              onClick={handleReplace}
              disabled={!selectedSlab || !selectedMaterial || replacing}
              className="w-full bg-[#D4AF37] hover:bg-[#B8962E] text-black font-medium py-3"
            >
              {replacing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <Wand2 className="w-4 h-4 mr-2" />
                  REPLACE SURFACE
                </>
              )}
            </Button>
          </div>
          
          {/* Right Panel - Preview */}
          <div className="lg:col-span-3">
            <div className="bg-[#111] rounded-xl border border-[#222] overflow-hidden">
              {/* Preview Area */}
              <div 
                ref={containerRef}
                className="relative aspect-video bg-[#0A0A0A]"
              >
                {loading ? (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <Loader2 className="w-8 h-8 animate-spin text-[#D4AF37]" />
                  </div>
                ) : result ? (
                  /* Before/After Comparison */
                  <div className="relative w-full h-full">
                    {/* Before Image (Left) */}
                    <div 
                      className="absolute inset-0"
                      style={{ clipPath: `inset(0 ${100 - sliderPosition}% 0 0)` }}
                    >
                      <img 
                        src={result.images.before}
                        alt="Before"
                        className="w-full h-full object-contain"
                      />
                      <span className="absolute top-4 left-4 px-2 py-1 bg-black/50 rounded text-xs">
                        BEFORE
                      </span>
                    </div>
                    
                    {/* After Image (Right) */}
                    <div className="absolute inset-0">
                      <img 
                        src={result.images.after}
                        alt="After"
                        className="w-full h-full object-contain"
                      />
                      <span className="absolute top-4 right-4 px-2 py-1 bg-black/50 rounded text-xs">
                        AFTER
                      </span>
                    </div>
                    
                    {/* Before Image Clip */}
                    <div 
                      className="absolute inset-0 overflow-hidden"
                      style={{ width: `${sliderPosition}%` }}
                    >
                      <img 
                        src={result.images.before}
                        alt="Before"
                        className="absolute inset-0 w-full h-full object-contain"
                        style={{ 
                          width: containerRef.current?.offsetWidth,
                          maxWidth: 'none'
                        }}
                      />
                    </div>
                    
                    {/* Slider Handle */}
                    <div 
                      className="absolute top-0 bottom-0 w-1 bg-[#D4AF37] cursor-ew-resize"
                      style={{ left: `${sliderPosition}%` }}
                    >
                      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-8 h-8 bg-[#D4AF37] rounded-full flex items-center justify-center">
                        <ChevronLeft className="w-3 h-3 text-black" />
                        <ChevronRight className="w-3 h-3 text-black" />
                      </div>
                    </div>
                    
                    {/* Slider Input (invisible) */}
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={sliderPosition}
                      onChange={(e) => setSliderPosition(parseInt(e.target.value))}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-ew-resize"
                    />
                  </div>
                ) : selectedSlab?.image?.url ? (
                  <div className="relative w-full h-full">
                    <img 
                      src={selectedSlab.image.url}
                      alt="Room"
                      className="w-full h-full object-contain"
                    />
                    <span className="absolute top-4 left-4 px-2 py-1 bg-black/50 rounded text-xs">
                      {selectedSlab.slab?.commercial_name || 'Slab'}
                    </span>
                  </div>
                ) : (
                  /* Empty State */
                  <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-500">
                    <ImageIcon className="w-12 h-12 mb-3 opacity-50" />
                    <p>Select a slab to preview</p>
                  </div>
                )}
              </div>
              
              {/* Metrics */}
              {result && (
                <div className="grid grid-cols-4 gap-4 p-4 border-t border-[#222]">
                  <div className="text-center">
                    <div className="text-2xl font-light text-[#D4AF37]">
                      {Math.round((result.metrics?.alignment_confidence || 0) * 100)}%
                    </div>
                    <div className="text-xs text-gray-500">Alignment</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-light text-[#D4AF37]">
                      {Math.round((result.metrics?.perspective_quality || 0) * 100)}%
                    </div>
                    <div className="text-xs text-gray-500">Perspective</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-light text-[#D4AF37]">
                      {Math.round((result.metrics?.continuity_score || 0) * 100)}%
                    </div>
                    <div className="text-xs text-gray-500">Continuity</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-light text-[#D4AF37]">
                      {Math.round((result.metrics?.realism_score || 0) * 100)}%
                    </div>
                    <div className="text-xs text-gray-500">Realism</div>
                  </div>
                </div>
              )}
              
              {/* Actions */}
              {result && (
                <div className="flex justify-between items-center p-4 border-t border-[#222]">
                  <div className="text-sm text-gray-400">
                    Material: <span className="text-white">{result.material?.name}</span>
                    <span className="mx-2">•</span>
                    Processed in <span className="text-white">{result.processing_time_ms}ms</span>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setResult(null)}
                      className="border-[#333] text-gray-300 hover:bg-[#1A1A1A]"
                    >
                      <RefreshCw className="w-4 h-4 mr-1" />
                      Reset
                    </Button>
                    <Button
                      size="sm"
                      onClick={downloadResult}
                      className="bg-[#D4AF37] hover:bg-[#B8962E] text-black"
                    >
                      <Download className="w-4 h-4 mr-1" />
                      Download
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default SurfaceReplacement;
